import React from 'react';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import { Calendar, Clock, Users, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const InstructorCalendarPage: React.FC = () => {
  const { instructor, isLoading } = useInstructorPermissions();

  if (isLoading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Calendrier</h1>
            <p className="text-gray-300 mt-1">
              Gérez vos sessions et événements
            </p>
          </div>
          <Button className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white border-0 shadow-md hover:shadow-lg">
            <Plus className="h-4 w-4" />
            Nouvelle session
          </Button>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700/40 rounded-2xl backdrop-blur-md shadow-lg hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Calendar className="h-5 w-5 text-indigo-400" />
                  Calendrier des sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Calendrier en cours de développement
                  </h3>
                  <p className="text-gray-300">
                    Cette fonctionnalité sera bientôt disponible pour planifier vos sessions.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {/* Sessions à venir */}
            <Card className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700/40 rounded-2xl backdrop-blur-md shadow-lg hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Clock className="h-5 w-5 text-amber-400" />
                  Prochaines sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-gray-900/40 border border-gray-700/50 rounded-xl">
                    <h4 className="font-medium text-white">Session Q&A React</h4>
                    <p className="text-sm text-gray-300">Aujourd'hui à 14h00</p>
                    <div className="flex items-center gap-1 mt-2 text-sm text-gray-300">
                      <Users className="h-3 w-3 text-gray-400" />
                      23 participants
                    </div>
                  </div>
                  <div className="p-3 bg-gray-900/40 border border-gray-700/50 rounded-xl">
                    <h4 className="font-medium text-white">Atelier TypeScript</h4>
                    <p className="text-sm text-gray-300">Demain à 10h00</p>
                    <div className="flex items-center gap-1 mt-2 text-sm text-gray-300">
                      <Users className="h-3 w-3 text-gray-400" />
                      15 participants
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions rapides */}
            <Card className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700/40 rounded-2xl backdrop-blur-md shadow-lg hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="text-white">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start border border-gray-700 text-gray-300 hover:bg-gray-800/50">
                  <Plus className="h-4 w-4 mr-2" />
                  Planifier une session
                </Button>
                <Button variant="outline" className="w-full justify-start border border-gray-700 text-gray-300 hover:bg-gray-800/50">
                  <Calendar className="h-4 w-4 mr-2" />
                  Voir le planning
                </Button>
                <Button variant="outline" className="w-full justify-start border border-gray-700 text-gray-300 hover:bg-gray-800/50">
                  <Users className="h-4 w-4 mr-2" />
                  Inviter des étudiants
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </InstructorLayout>
  );
};

export default InstructorCalendarPage;
